import { Controller } from "@hotwired/stimulus"

const INDICATOR_ID = "chat-ai-typing-indicator"

export default class extends Controller {
  static targets = ["input"]
  static values = {
    roomAiEnabled: Boolean,
    mention: String,
    assistantAvatar: String
  }

  connect() {
    this.messagesObserver = null
    this.waitTimeout = null
    this.submitStartMaxMessageId = null
    this.pendingInputValue = ""
  }

  disconnect() {
    this.stopObservingMessages()
    this.removeIndicator()
    this.clearWaitTimeout()
  }

  submitStart() {
    this.pendingInputValue = this.rawInputValue()
    this.clearInputField()

    if (!this.aiRequested(this.pendingInputValue)) {
      this.removeIndicator()
      this.stopObservingMessages()
      return
    }

    this.submitStartMaxMessageId = this.currentMaxMessageId()
    this.renderIndicator()
    this.observeMessagesForAssistantReply()
    this.startWaitTimeout()
  }

  submitEnd(event) {
    if (this.submissionSucceeded(event)) {
      this.pendingInputValue = ""
      return
    }

    this.restoreInputField()
    this.removeIndicator()
    this.stopObservingMessages()
    this.clearWaitTimeout()
  }

  aiRequested(contentOverride = null) {
    if (this.roomAiEnabledValue) return true

    const mention = this.normalizedMention()
    if (mention.length === 0) return false

    const content = this.inputText(contentOverride)
    if (content.length === 0) return false

    return content.startsWith(mention.toLowerCase())
  }

  normalizedMention() {
    return String(this.mentionValue || "@ai").trim().toLowerCase()
  }

  inputText(contentOverride = null) {
    if (contentOverride !== null) return String(contentOverride || "").trim()
    if (!this.hasInputTarget) return ""
    return String(this.inputTarget.value || "").trim()
  }

  rawInputValue() {
    if (!this.hasInputTarget) return ""
    return String(this.inputTarget.value || "")
  }

  clearInputField() {
    if (!this.hasInputTarget) return
    this.inputTarget.value = ""
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  restoreInputField() {
    if (!this.hasInputTarget) return
    if (this.pendingInputValue.length === 0) return

    this.inputTarget.value = this.pendingInputValue
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.focus()
  }

  submissionSucceeded(event) {
    if (!event) return true

    if (typeof event.detail?.success === "boolean") return event.detail.success

    const fetchResponse = event.detail?.fetchResponse
    if (typeof fetchResponse?.succeeded === "boolean") return fetchResponse.succeeded

    const status = fetchResponse?.statusCode || fetchResponse?.status
    if (typeof status === "number") return status >= 200 && status < 300

    return true
  }

  messagesElement() {
    return document.getElementById("messages")
  }

  renderIndicator() {
    const container = this.messagesElement()
    if (!container) return

    this.removeIndicator()

    const wrapper = document.createElement("div")
    wrapper.id = INDICATOR_ID
    wrapper.className = "message assistant typing-indicator"
    const avatar = this.escapeHtml(this.assistantAvatarValue || "/assets/avatar.png")
    wrapper.innerHTML = `
      <div class="content-container">
        <div class="content assistant-content">
          <img class="rounded-circle chat-avatar-inline" src="${avatar}" width="20" height="20" alt="AI avatar" />
          <div class="message-text">
            <span class="typing-dots typing-dots-live" aria-label="AI is typing" role="status" aria-live="polite">
              <span></span><span></span><span></span>
            </span>
          </div>
        </div>
        <div class="author">AI Assistant</div>
      </div>
    `

    container.appendChild(wrapper)
    container.scrollTop = container.scrollHeight
  }

  removeIndicator() {
    const indicator = document.getElementById(INDICATOR_ID)
    if (indicator) indicator.remove()
  }

  observeMessagesForAssistantReply() {
    const container = this.messagesElement()
    if (!container) return

    this.stopObservingMessages()

    this.messagesObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const addedElements = Array.from(mutation.addedNodes).filter((node) => node.nodeType === Node.ELEMENT_NODE)
        const hasAssistantReply = this.hasAssistantReplyAfterSubmit(addedElements)

        if (hasAssistantReply) {
          this.removeIndicator()
          this.stopObservingMessages()
          this.clearWaitTimeout()
          return
        }
      }
    })

    this.messagesObserver.observe(container, { childList: true, subtree: false })
  }

  stopObservingMessages() {
    if (!this.messagesObserver) return
    this.messagesObserver.disconnect()
    this.messagesObserver = null
  }

  startWaitTimeout() {
    this.clearWaitTimeout()
    this.waitTimeout = window.setTimeout(() => {
      this.removeIndicator()
      this.stopObservingMessages()
      this.waitTimeout = null
    }, 90000)
  }

  clearWaitTimeout() {
    if (!this.waitTimeout) return
    window.clearTimeout(this.waitTimeout)
    this.waitTimeout = null
  }

  hasAssistantReplyAfterSubmit(addedElements) {
    const baselineId = Number.isFinite(this.submitStartMaxMessageId) ? this.submitStartMaxMessageId : 0

    return addedElements.some((element) => {
      const node = /** @type {Element} */ (element)
      const candidates = []

      if (node.matches(".message.assistant:not(.typing-indicator)")) {
        candidates.push(node)
      }

      const descendants = node.querySelectorAll ? Array.from(node.querySelectorAll(".message.assistant:not(.typing-indicator)")) : []
      descendants.forEach((candidate) => candidates.push(candidate))

      return candidates.some((candidate) => this.messageIdForElement(candidate) > baselineId)
    })
  }

  currentMaxMessageId() {
    const container = this.messagesElement()
    if (!container) return 0

    const ids = Array.from(container.querySelectorAll(".message[data-message-id]"))
      .map((element) => this.messageIdForElement(element))
      .filter((id) => Number.isFinite(id) && id > 0)

    if (ids.length === 0) return 0
    return Math.max(...ids)
  }

  messageIdForElement(element) {
    const raw = element?.dataset?.messageId
    const parsed = Number.parseInt(String(raw || ""), 10)
    return Number.isFinite(parsed) ? parsed : 0
  }

  escapeHtml(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
